// Packages
import React from 'react';
import { gql } from '@apollo/client';
import { Box, Typography, List, ListItem } from '@mui/material';

// Styles
import parentStyles from '../PermissionsModal.module.scss';
import childStyles from './ConfirmPermissionsChangeModal.module.scss';

// Markup
import { Button } from '../../../blocks/Button';
import MuiModal from '../../../mui/MuiModal/MuiModal';
import { VendorProfile, VendorProfileProps } from '../VendorProfile';

// Utilities
import { LABELS } from '../SetPermissionsModal/PermissionsToggles';
import { camelize } from '../../../lib/helpers';

const confirmPermissionsChangeModalVendorFragment = gql`
	fragment confirmPermissionsChangeModalVendor on Organization {
		...vendorProfileVendor
	}
	${ VendorProfile.fragments.vendorProfileVendorFragment }
`;

export type ConfirmPermissionsChangeModalProps = {
	open: boolean;
	vendor: VendorProfileProps['vendor'];
	vendorWithPermissions: VendorProfileProps['vendor'] | undefined;
	isLoading: boolean;
	onCancel: () => void;
	onConfirm: () => Promise<void>;
};

/**
 * Confirmation modal for permissions change
 */
const ConfirmPermissionsChangeModal = ( {
	open,
	vendor,
	vendorWithPermissions,
	isLoading,
	onCancel,
	onConfirm,
}: ConfirmPermissionsChangeModalProps ): React.JSX.Element => (
	<MuiModal
		muiModalProps={ {
			open,
			onClose: onCancel,
			className: parentStyles.permissionsModal,
		} }
		title={
			<Typography variant='h5' component='h2' className={ parentStyles.header }>
				Do you want to change which vendor has permissions for you?
			</Typography>
		}
	>
		<Box className={ parentStyles.permissionsModalContent }>
			<VendorProfile vendor={ vendor } />
			<Box className={ childStyles.description }>
				<Typography>
					Assigning permissions to <strong>{ vendor.name }</strong> will remove
					all permissions from <strong>{ vendorWithPermissions?.name }</strong>.
				</Typography>
			</Box>
			<Typography className={ childStyles.listHeader }>
				They will no longer be able to
			</Typography>
			<List className={ childStyles.list }>
				{ Object.values( LABELS ).map( ( label ) => (
					<ListItem key={ camelize( label ) }>{ label }</ListItem>
				) ) }
			</List>
			<Box className={ parentStyles.buttonRow }>
				<Button variant='outlined' onClick={ onCancel }>
					Cancel
				</Button>
				<Button
					variant='contained'
					loading={ isLoading }
					className={ parentStyles.assignButton }
					onClick={ onConfirm }
				>
					Confirm
				</Button>
			</Box>
		</Box>
	</MuiModal>
);

ConfirmPermissionsChangeModal.fragments = {
	confirmPermissionsChangeModalVendorFragment,
};

export default ConfirmPermissionsChangeModal;
